import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, AlertCircle, ArrowDownRight, CheckCircle2,
  Calendar, Search, Filter, Plus, FileText, Paperclip, RefreshCw
} from 'lucide-react';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/format';
import FinancialHeader from './FinancialHeader';
import NewTransactionModal from './NewTransactionModal';
import SettleModal from './SettleModal';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

export default function Payables() {
  const [searchParams] = useSearchParams();
  const urlStatus = searchParams.get('status');
  const [ownerCompanyId, setOwnerCompanyId] = useState('all');
  const [ownerCompanies, setOwnerCompanies] = useState([]);
  const [activeTab, setActiveTab] = useState(urlStatus || 'pending'); // 'pending', 'overdue', 'today', 'next7', 'paid', 'all'
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');

  // Sync tab with URL if param changes
  useEffect(() => {
    const s = searchParams.get('status');
    if (s && ['pending', 'overdue', 'today', 'next7', 'paid', 'all'].includes(s)) {
      setActiveTab(s);
    }
  }, [searchParams]);

  // Modals
  const [newTxModalOpen, setNewTxModalOpen] = useState(false);
  const [settleModalOpen, setSettleModalOpen] = useState(false);
  const [selectedTxForSettle, setSelectedTxForSettle] = useState(null);

  // Load companies
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/owner-companies');
        const comps = res.data?.data || res.data?.items || (Array.isArray(res.data) ? res.data : []);
        setOwnerCompanies(comps);
      } catch (err) {
        console.error('Erro ao buscar empresas', err);
      }
    })();
  }, []);

  const loadPayables = useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().substring(0, 10);
      const in7Days = new Date(Date.now() + 7 * 86400000).toISOString().substring(0, 10);

      const params = {
        type: 'expense',
        limit: 100,
      };

      if (ownerCompanyId && ownerCompanyId !== 'all') params.owner_company_id = ownerCompanyId;
      if (search.trim()) params.search = search.trim();

      if (activeTab === 'pending') {
        params.payment_status = 'pending'; // Returns all unpaid (pending + overdue)
      } else if (activeTab === 'overdue') {
        params.payment_status = 'overdue';
      } else if (activeTab === 'today') {
        params.payment_status = 'pending';
        params.start_date = today;
        params.end_date = today;
      } else if (activeTab === 'next7') {
        params.payment_status = 'pending';
        params.start_date = today;
        params.end_date = in7Days;
      } else if (activeTab === 'paid') {
        params.payment_status = 'paid';
      }

      const res = await api.get('/financial/transactions', { params });
      const raw = res.data;
      const list = raw?.items || raw?.data || (Array.isArray(raw) ? raw : []);
      setTransactions(list);
    } catch (err) {
      console.error('Erro ao carregar contas a pagar', err);
      toast.error('Erro ao carregar contas a pagar');
    } finally {
      setLoading(false);
    }
  }, [ownerCompanyId, activeTab, search]);

  useEffect(() => {
    loadPayables();
  }, [loadPayables]);

  const handleSyncModules = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/financial/sync');
      toast.success(res.data?.message || res.data?._message || 'Módulos sincronizados com o Financeiro!');
      loadPayables();
    } catch (err) {
      console.error('Erro ao sincronizar', err);
      toast.error('Erro ao sincronizar dados operacionais');
    } finally {
      setSyncing(false);
    }
  };

  const handleSettle = (tx) => {
    setSelectedTxForSettle(tx);
    setSettleModalOpen(true);
  };

  const handleRevert = async (tx) => {
    if (!window.confirm(`Deseja estornar a baixa de "${tx.description}"?`)) return;
    try {
      await api.post(`/financial/transactions/${tx.id}/revert`);
      toast.success('Baixa estornada com sucesso');
      loadPayables();
    } catch {
      toast.error('Erro ao estornar');
    }
  };

  // Summaries
  const totalAmount = transactions.reduce((acc, t) => acc + (t.amount || 0), 0);

  return (
    <div className="space-y-6">
      <FinancialHeader
        title="Contas a Pagar"
        subtitle="Gerenciamento de pagamentos, licenças, manutenções, combustíveis e despesas operacionais"
        ownerCompanyId={ownerCompanyId}
        setOwnerCompanyId={setOwnerCompanyId}
        ownerCompanies={ownerCompanies}
        onNewTransaction={() => setNewTxModalOpen(true)}
        onRefresh={loadPayables}
      />

      {/* Tabs and Quick Search */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full sm:w-auto">
          <TabsList className="grid grid-cols-3 sm:flex">
            <TabsTrigger value="pending" className="text-xs">Em Aberto</TabsTrigger>
            <TabsTrigger value="overdue" className="text-xs text-red-600 dark:text-red-400 font-semibold">Vencidas</TabsTrigger>
            <TabsTrigger value="today" className="text-xs font-semibold">Vence Hoje</TabsTrigger>
            <TabsTrigger value="next7" className="text-xs">Próximos 7 Dias</TabsTrigger>
            <TabsTrigger value="paid" className="text-xs">Pagas</TabsTrigger>
            <TabsTrigger value="all" className="text-xs">Todas</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-neutral-400" />
          <Input
            type="text"
            placeholder="Filtrar por fornecedor, descrição..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 text-xs"
          />
        </div>
      </div>

      {/* Totalizador rápido */}
      <div className="bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-red-100 dark:bg-red-950/50 flex items-center justify-center text-red-600 dark:text-red-400">
            <ArrowDownRight className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs text-neutral-500 font-medium">Total das contas listadas ({transactions.length} registros)</p>
            <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{formatCurrency(totalAmount)}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={handleSyncModules}
            disabled={syncing}
            size="sm"
            variant="outline"
            className="text-xs gap-1.5"
            title="Sincronizar despesas com frotas, licenças e documentos"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando...' : 'Sincronizar Módulos'}
          </Button>

          <Button
            onClick={() => setNewTxModalOpen(true)}
            size="sm"
            className="bg-brand hover:bg-[#A81E23] text-white text-xs gap-1.5"
          >
            <Plus className="h-4 w-4" /> Nova Despesa
          </Button>
        </div>
      </div>

      {/* Tabela de Contas a Pagar */}
      <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-neutral-50 dark:bg-neutral-800/50 text-neutral-500 font-semibold border-b border-neutral-200 dark:border-neutral-800">
              <tr>
                <th className="py-3 px-4">Vencimento</th>
                <th className="py-3 px-4">Descrição da Despesa</th>
                <th className="py-3 px-4">Fornecedor / Veículo</th>
                <th className="py-3 px-4">Categoria / Origem</th>
                <th className="py-3 px-4">Empresa (CNPJ)</th>
                <th className="py-3 px-4 text-right">Valor</th>
                <th className="py-3 px-4 text-center">Status</th>
                <th className="py-3 px-4 text-center">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={8} className="py-4 px-4">
                      <div className="h-4 bg-neutral-100 dark:bg-neutral-800 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-neutral-400">
                    Nenhuma conta a pagar encontrada para esta seleção.
                  </td>
                </tr>
              ) : (
                transactions.map((tx) => {
                  const todayStr = new Date().toISOString().substring(0, 10);
                  const isPaid = tx.payment_status === 'paid';
                  const isOverdue = !isPaid && tx.due_date && tx.due_date < todayStr;
                  const isToday = !isPaid && tx.due_date === todayStr;

                  return (
                    <tr key={tx.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/40 transition-colors">
                      {/* Vencimento */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <div className={`font-semibold ${isOverdue ? 'text-red-600 dark:text-red-400' : isToday ? 'text-amber-600 dark:text-amber-400' : 'text-neutral-900 dark:text-neutral-100'}`}>
                          {formatDate(tx.due_date)}
                        </div>
                        {isPaid && tx.payment_date && (
                          <div className="text-[10px] text-emerald-600">
                            Pago em: {formatDate(tx.payment_date)}
                          </div>
                        )}
                        {isOverdue && (
                          <div className="text-[10px] text-red-500 font-medium">Vencida!</div>
                        )}
                        {isToday && (
                          <div className="text-[10px] text-amber-600 font-semibold">Vence Hoje</div>
                        )}
                      </td>

                      {/* Descrição */}
                      <td className="py-3 px-4 max-w-xs">
                        <p className="font-medium text-neutral-900 dark:text-neutral-100 truncate" title={tx.description}>
                          {tx.description}
                        </p>
                        {tx.total_installments > 1 && (
                          <span className="text-[10px] text-neutral-500 font-medium">
                            Parcela {tx.installment_number} de {tx.total_installments}
                          </span>
                        )}
                      </td>

                      {/* Fornecedor / Veículo */}
                      <td className="py-3 px-4 whitespace-nowrap text-neutral-600 dark:text-neutral-300">
                        {tx.supplier_name ? (
                          <span title={`Fornecedor: ${tx.supplier_name}`}>📦 {tx.supplier_name}</span>
                        ) : tx.vehicle_plate ? (
                          <span title={`Veículo: ${tx.vehicle_plate}`}>🚌 {tx.vehicle_plate}</span>
                        ) : (
                          <span className="text-neutral-400">-</span>
                        )}
                      </td>

                      {/* Categoria / Origem */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className="text-neutral-600 dark:text-neutral-300">{tx.category_name || 'Despesa'}</span>
                        {tx.origin && tx.origin !== 'manual' && (
                          <Badge variant="outline" className="ml-1 text-[9px] py-0 px-1 border-neutral-300">
                            {tx.origin === 'license' ? 'Licença' : tx.origin === 'maintenance_order' ? 'OS' : tx.origin === 'fuel_record' ? 'Combustível' : tx.origin}
                          </Badge>
                        )}
                      </td>

                      {/* Empresa */}
                      <td className="py-3 px-4 whitespace-nowrap text-neutral-600 dark:text-neutral-300">
                        {tx.owner_company_name || 'Dany Trans'}
                      </td>

                      {/* Valor */}
                      <td className="py-3 px-4 whitespace-nowrap text-right font-bold text-neutral-900 dark:text-neutral-100">
                        {formatCurrency(isPaid && tx.net_amount ? tx.net_amount : tx.amount)}
                      </td>

                      {/* Status */}
                      <td className="py-3 px-4 whitespace-nowrap text-center">
                        {isPaid ? (
                          <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200">
                            Pago
                          </Badge>
                        ) : isOverdue ? (
                          <Badge className="bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300 border-red-200">
                            Vencido
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-200">
                            Pendente
                          </Badge>
                        )}
                      </td>

                      {/* Ações */}
                      <td className="py-3 px-4 whitespace-nowrap text-center">
                        {!isPaid ? (
                          <Button
                            size="xs"
                            onClick={() => handleSettle(tx)}
                            className="h-7 px-2.5 text-[11px] font-semibold bg-brand hover:bg-[#A81E23] text-white"
                          >
                            Dar Baixa
                          </Button>
                        ) : (
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={() => handleRevert(tx)}
                            className="h-7 text-[11px] text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          >
                            Estornar
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      <NewTransactionModal
        open={newTxModalOpen}
        onOpenChange={setNewTxModalOpen}
        onSuccess={loadPayables}
        defaultType="expense"
      />

      <SettleModal
        open={settleModalOpen}
        onOpenChange={setSettleModalOpen}
        transaction={selectedTxForSettle}
        onSuccess={loadPayables}
      />
    </div>
  );
}
